import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { getRejoinPolicyApi } from "api/user";

// 회원 탈퇴 확인창 (2026-10-20)
// - 안내 문구는 서버(GET /api/get/rejoinPolicy)의 withdrawNotice 를 그대로 보여준다.
//   재가입 제한 일수는 관리자 페이지에서 바뀌므로 화면에 적지 않는다
// - 탈퇴 사유 항목·형식은 앱(Flutter)과 같게 맞춘다: 하나만 선택(다시 누르면 해제), 선택하지 않아도 탈퇴 가능,
//   직접 입력 최대 200자, 보내는 값은 "선택사유 / 직접입력" (둘 중 있는 것만), 아무것도 없으면 ""

const FALLBACK_POLICY = {
  supportPhone: "070-7575-2057",
  withdrawNotice:
    "탈퇴 후에는 다시 로그인할 수 없으며,\n탈퇴 후 7일이 지나야 다시 가입할 수 있습니다.\n그 전에 다시 가입하시려면 (주)엠아이솔루션(070-7575-2057)으로 연락해 주세요.",
};

const REASONS = [
  { label: "자주 이용하지 않아요" },
  { label: "원하는 매칭이 잘 이루어지지 않아요" },
  { label: "포인트 비용이 부담돼요", expertOnly: true },
  { label: "앱 사용이 불편해요" },
  { label: "알림이 너무 많아요" },
  { label: "기타" },
];

const DETAIL_MAX = 200;

// 안내 문구 속 전화번호를 눌러서 걸 수 있게 한다
function NoticeText({ text, phone }) {
  if (!phone || !text.includes(phone)) return text;
  const parts = text.split(phone);
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <a href={`tel:${phone.replace(/\D/g, "")}`} style={{ color: "#047857", fontWeight: 700 }}>
          {phone}
        </a>
      )}
    </span>
  ));
}

export default function WithdrawDialog({ open, onClose, onConfirm, isExpert }) {
  const [policy, setPolicy] = useState(FALLBACK_POLICY);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected("");
    setDetail("");
    let cancelled = false;
    getRejoinPolicyApi()
      .then((p) => {
        if (!cancelled && p && p.withdrawNotice) setPolicy(p);
      })
      .catch(() => {}); // 실패하면 기본 문구(7일)를 그대로 쓴다
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reasons = REASONS.filter((r) => !r.expertOnly || isExpert);
  const reason = [selected, detail.trim()].filter(Boolean).join(" / ");

  return (
    <Dialog open={open} onClose={onClose} sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
      <div
        className="md:w-[400px] w-[310px]"
        style={{ display: "flex", flexDirection: "column", padding: 24, background: "#fff", gap: 16 }}
      >
        <div className="f20_111827_W600" style={{ textAlign: "center", marginTop: 8 }}>
          정말 탈퇴하시겠습니까?
        </div>
        <div
          className="f14_4B5563_W500"
          style={{ textAlign: "center", whiteSpace: "pre-line", wordBreak: "keep-all", lineHeight: 1.6 }}
        >
          <NoticeText text={policy.withdrawNotice} phone={policy.supportPhone} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
            탈퇴 사유 <span style={{ fontWeight: 500, color: "#6B7280" }}>(선택)</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {reasons.map(({ label }) => {
              const on = selected === label;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setSelected(on ? "" : label)}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 9999,
                    border: `1px solid ${on ? "#2E4A24" : "#E5E7EB"}`,
                    background: on ? "#2E4A24" : "#fff",
                    color: on ? "#fff" : "#374151",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value.slice(0, DETAIL_MAX))}
            placeholder="자세한 이유를 적어주세요 (선택)"
            rows={3}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 14,
              fontFamily: "inherit",
              resize: "none",
              outline: "none",
            }}
          />
          <div style={{ alignSelf: "flex-end", fontSize: 11, color: "#9CA3AF" }}>
            {detail.length}/{DETAIL_MAX}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
          <div
            onClick={() => onClose()}
            className="f16_2E4A24_W500"
            style={{
              flex: 1,
              padding: "13px 0",
              border: "1px solid #2E4A24",
              borderRadius: 8,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            아니오
          </div>
          <div
            onClick={() => onConfirm(reason)}
            className="f16_FFFFFF_W500"
            style={{
              flex: 1,
              padding: "13px 0",
              background: "#2E4A24",
              borderRadius: 8,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            탈퇴하기
          </div>
        </div>
      </div>
    </Dialog>
  );
}
